<template>
  <div
    v-if="show"
    class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 overflow-y-scroll flex items-center justify-center text-base"
  >
    <div class="bg-white rounded-lg shadow-xl w-1/2 h-[40vh] overflow-y-auto">
      <div class="flex items-center justify-between p-4 border-b">
        <h4 class="text-2xl text-gray-800 font-bold">Edit Beneficiary</h4>
        <button
          type="button"
          class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-yellow-500"
          @click="handleCloseModal"
          aria-label="Close"
        >
          <span class="sr-only">Dismiss</span>
          <X :size="24" />
        </button>
      </div>
      <form
        @submit.prevent="handleSubmitForm"
        class="space-y-4 p-6"
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-20 gap-y-6 px-4 md:px-20">

          <!-- Beneficiary Name -->
          <div>
            <label for="name" class="block text-sm font-medium text-gray-700">
              Beneficiary Name <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              v-model="formData.name"
              placeholder="Enter Beneficiary name.."
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            />
            <span class="text-sm text-red-500" v-if="errors.name">{{ errors.name }}</span>
          </div>

          <!-- ID Number -->
          <div>
            <label for="id_number" class="block text-sm font-medium text-gray-700">
              ID Number <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="id_number"
              v-model="formData.id_number"
              placeholder="Enter ID.."
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            />
            <span class="text-sm text-red-500" v-if="errors.id_number">{{ errors.id_number }}</span>
          </div>

          <!-- Cover Amount -->
          <div>
            <label for="cover_amount" class="block text-sm font-medium text-gray-700">
              Cover Amount
            </label>
            <input
              type="number"
              id="cover_amount"
              v-model.number="formData.cover_amount"
              placeholder="Enter cover amount..."
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            />
            <span class="text-sm text-red-500" v-if="errors.cover_amount">
              {{ errors.cover_amount }}
            </span>
          </div>

          <!-- Valid Until -->
          <div>
            <label for="validUntil" class="block text-sm font-medium text-gray-700">
              Valid Until
            </label>
            <input
              type="date"
              id="validUntil"
              v-model="formData.validity_end_date"
              placeholder="Select Validity End Date.."
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            />
            <span class="text-sm text-red-500" v-if="errors.validity_end_date">
              {{ errors.validity_end_date }}
            </span>
          </div>
        </div>

        <div class="flex justify-end mt-6 gap-y-6 px-4 md:px-20 pb-6">
          <button
            type="submit"
            :disabled="showLoader"
            class="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50"
          >
            <span v-if="!showLoader"> Submit </span>
            <span v-else class="flex items-center">
              <svg
                class="animate-spin h-5 w-5 text-white mr-2"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                  fill="none"
                />
              </svg>
              Updating...
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
<script setup>
import { ref, watch, defineProps, defineEmits } from "vue";
import { X } from "lucide-vue-next"
import apiClient from "@/services/api"
import { useToast } from "vue-toast-notification"

const toast = useToast({
  position: 'top-right',
  duration: 3000,
  dismissible: true,
  pauseOnHover: true,
  type: 'default',
});

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  beneficiary: {
    type: Object,
    required: true
  }
});

const emits = defineEmits(['close', 'updated']);

// Form state
const formData = ref({
  id: '',
  name: '',
  id_number: '',
  cover_amount:null,
  validity_end_date: null,
});

// UI State
const showLoader = ref(false);
const errors = ref({});

// Utility to format date for input type="date"
const formatDateForInput = (dateString) => {
  console.log('date', dateString)
  if (!dateString) return null;
  const date = new Date(dateString)

  return date.toISOString().split('T')[0];
}

watch(() => props.beneficiary, (newItem) => {
  if (newItem) {
    formData.value = {
      id: newItem.id,
      name: newItem.name || '',
      id_number: newItem.id_number,
      validity_end_date: formatDateForInput(newItem.validity_end_date),
      cover_amount: Number(newItem.cover_amount),
    };
  }
}, { immediate: true });

const validateForm = () => {
  errors.value = {}; // Reset errors
  let isValid = true;

  // Validate name
  if (!formData.value.name || formData.value.name.trim() === '') {
    errors.value.name = 'Beneficiary name is required.';
    isValid = false;
  }

  // Validate id_number
  if (!formData.value.id_number || formData.value.id_number.trim() === '') {
    errors.value.id_number = 'ID number is required.';
    isValid = false;
  }

  // Validate cover_amount (optional, but must be positive if provided)
  if (formData.value.cover_amount !== null && formData.value.cover_amount !== '') {
    if (isNaN(formData.value.cover_amount) || formData.value.cover_amount < 0) {
      errors.value.cover_amount = 'Cover amount must be a positive number.';
      isValid = false;
    }
  }

  // Validate validity_end_date (optional, but must not be in the past if provided)
  if (formData.value.validity_end_date) {
    const selectedDate = new Date(formData.value.validity_end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    if (isNaN(selectedDate.getTime())) {
      errors.value.validity_end_date = 'Please enter a valid date.';
      isValid = false;
    } else if (selectedDate < today) {
      errors.value.validity_end_date = 'Validity end date cannot be in the past.';
      isValid = false;
    }
  }

  return isValid;
};

const handleSubmitForm = async () => {
  if (!validateForm()) {
    toast.error("Please address the errors on the form.")
    return;
  }

  showLoader.value = true;

  console.log("form data", formData.value)

  try {
    const payload = {
      id: formData.value.id,
      name: formData.value.name,
      id_number: formData.value.id_number,
      cover_amount: formData.value.cover_amount,
      validity_end_date: formData.value.validity_end_date
    };

    const response = await apiClient.updateBeneficiary(payload.id, payload)

    if (response.success) {
      toast.success(response.message);
      handleSuccessfulUpdate();
      handleCloseModal();
    } else {
      toast.error("Failed to update scheme " + response.message)
    }
  } catch (error) {
    toast.error("Error: " + error.message)
    console.log("Error updating scheme", error)
  } finally {
    showLoader.value = false;
  }
}

const handleCloseModal = () => {
  emits('close');
};

const handleSuccessfulUpdate = () => {
  emits('updated')
};
</script>